import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MessagingUpgradeDialog, isUpsellError } from '@/components/employer/MessagingUpgradeDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { schools, messages } from '@/lib/api';
import { getStoredUser } from '@/lib/auth';
import PlanLimitTooltip from '@/components/PlanLimitTooltip';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { 
  ArrowLeft, Clock, Loader2, User, Ban, CheckCircle, 
  MessageSquare, FileText, Send, X, Archive, ArchiveRestore, BarChart3
} from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import AppFooter from '@/components/AppFooter';

export default function TOCManagement() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const { planHasFeature, upgradeTooltipMessage, refreshStatus } = usePlanLimits();
  const [user] = useState(getStoredUser());
  const [isLoading, setIsLoading] = useState(true);
  const [school, setSchool] = useState(null);
  const [tocList, setTocList] = useState([]);
  const [archivedTocList, setArchivedTocList] = useState([]);
  const [selectedTOC, setSelectedTOC] = useState(null);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showMessagingUpgrade, setShowMessagingUpgrade] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [messageText, setMessageText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [isArchiving, setIsArchiving] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [schoolRes, tocsRes, archivedRes] = await Promise.all([
          schools.get(schoolId),
          schools.getTOCs(schoolId),
          schools.getArchivedTOCs(schoolId).catch(() => ({ data: [] }))
        ]);
        setSchool(schoolRes.data);
        setTocList(tocsRes.data);
        setArchivedTocList(archivedRes.data);
      } catch (error) {
        toast.error('Failed to load data');
        navigate('/employer');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [schoolId, navigate]);

  // Scroll to sub card if hash is present (e.g., #sub-user_abc123)
  useEffect(() => {
    if (!isLoading && window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      }
    }
  }, [isLoading]);

  
  const handleStatusChange = async (tocId, newStatus) => {
    try {
      await schools.updateTOCStatus(schoolId, tocId, newStatus);
      setTocList(prev => prev.map(t => 
        t.user_id === tocId ? { ...t, status: newStatus } : t
      ));
      toast.success(`Sub ${newStatus === 'blocked' ? 'blocked' : 'unblocked'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  const handleSaveNotes = async () => {
    if (!selectedTOC) return;
    setIsSaving(true);
    
    try {
      await schools.updateTOCNotes(schoolId, selectedTOC.user_id, noteText);
      setTocList(prev => prev.map(t => 
        t.user_id === selectedTOC.user_id ? { ...t, private_notes: noteText } : t
      ));
      toast.success('Notes saved');
      setShowNotesDialog(false);
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleOpenNotes = (toc) => {
    setSelectedTOC(toc);
    setNoteText(toc.private_notes || '');
    setShowNotesDialog(true);
  };
  
  const handleOpenMessage = async (toc) => {
    setSelectedTOC(toc);
    setMessageText('');
    setShowMessageDialog(true);
    
    try {
      const res = await messages.list(schoolId, toc.user_id);
      setConversation(res.data);
    } catch (error) {
      // Silent fail - empty conversation will be shown
    }
  };
  
  // Poll for new messages when message dialog is open
  useEffect(() => {
    if (!showMessageDialog || !selectedTOC) return;
    
    const pollMessages = async () => {
      try {
        const res = await messages.list(schoolId, selectedTOC.user_id);
        setConversation(res.data);
      } catch (_e) {
        // Polling failure - will retry on next interval
      }
    };
    
    // Poll every 5 seconds
    const interval = setInterval(pollMessages, 5000);
    
    return () => clearInterval(interval);
  }, [showMessageDialog, selectedTOC, schoolId]);
  
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedTOC) return;
    // Head Start: intercept send, keep the draft and show the upgrade prompt.
    // plan_id is the computed owner-plan field on the school payload (schools
    // no longer carry subscription_plan — Oct 2026 subscription-model refactor).
    if (!planHasFeature(school?.plan_id, 'in_app_messaging')) {
      setShowMessagingUpgrade(true);
      return;
    }
    setIsSaving(true);
    
    try {
      const res = await messages.sendToTOC(selectedTOC.user_id, schoolId, messageText);
      setConversation(prev => [res.data, ...prev]);
      setMessageText('');
      toast.success('Message sent');
    } catch (error) {
      if (isUpsellError(error)) {
        setShowMessagingUpgrade(true);
      } else {
        toast.error('Failed to send message');
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleArchiveTOC = async (tocId) => {
    setIsArchiving(true);
    try {
      await schools.archiveTOC(schoolId, tocId);
      refreshStatus(); // active-Sub count dropped — keep isAtLimit('subs') in sync
      // Move TOC from active to archived list
      const archivedToc = tocList.find(t => t.user_id === tocId);
      if (archivedToc) {
        setTocList(prev => prev.filter(t => t.user_id !== tocId));
        setArchivedTocList(prev => [...prev, { ...archivedToc, status: 'archived' }]);
      }
      toast.success('Sub archived');
    } catch (error) {
      toast.error('Failed to archive Sub');
    } finally {
      setIsArchiving(false);
    }
  };
  
  const handleUnarchiveTOC = async (tocId) => {
    setIsArchiving(true);
    try {
      await schools.unarchiveTOC(schoolId, tocId);
      refreshStatus(); // active-Sub count rose — keep isAtLimit('subs') in sync
      // Move TOC from archived back to active list
      const restoredToc = archivedTocList.find(t => t.user_id === tocId);
      if (restoredToc) {
        setArchivedTocList(prev => prev.filter(t => t.user_id !== tocId));
        setTocList(prev => [...prev, { ...restoredToc, status: 'active' }]);
      }
      toast.success('Sub restored to active list');
    } catch (error) {
      toast.error('Failed to restore Sub');
    } finally {
      setIsArchiving(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/employer">
            <Button variant="ghost" size="icon" className="text-white hover:bg-slate-800">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <BrandLogo variant="white" size="lg" />
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
            Manage Subs - {school?.name}
          </h1>
          <p className="text-slate-600 mt-1">
            {tocList.length} active, {archivedTocList.length} archived
          </p>
        </div>
        
        <Tabs defaultValue="active" className="space-y-6">
          <TabsList>
            <TabsTrigger value="active" data-testid="active-subs-tab">
              Active Subs ({tocList.length})
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="archived-subs-tab">
              Archived Subs ({archivedTocList.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="active">
        {tocList.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <User className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Subs connected yet</h3>
              <p className="text-sm text-slate-500">
                Share your school code with substitute teachers to build your list
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {tocList.map((toc) => (
              <Card key={toc.user_id} id={`sub-${toc.user_id}`} className="border-slate-200 scroll-mt-4">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* TOC Info */}
                    <div className="flex items-center gap-4 flex-1">
                      <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                        {toc.picture ? (
                          <img src={toc.picture} alt={toc.name} className="h-12 w-12 rounded-full" />
                        ) : (
                          <User className="h-6 w-6 text-slate-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{toc.name}</p>
                          <Badge className={toc.status === 'blocked' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}>
                            {toc.status === 'blocked' ? 'Blocked' : 'Active'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">{toc.email}</p>
                      </div>
                    </div>
                    
                    {/* Stats - View Details */}
                    <div className="flex items-center">
                      <Link to={`/employer/analytics/${schoolId}#sub-detail-${toc.user_id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          data-testid={`view-details-${toc.user_id}`}
                        >
                          <BarChart3 className="h-3.5 w-3.5 mr-1" />
                          View Details
                        </Button>
                      </Link>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenNotes(toc)}
                        data-testid={`notes-btn-${toc.user_id}`}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Notes
                      </Button>
                      
                      <PlanLimitTooltip
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenMessage(toc)}
                        data-testid={`message-btn-${toc.user_id}`}
                        disabled={!planHasFeature(school?.plan_id, 'in_app_messaging')}
                        message={upgradeTooltipMessage('in_app_messaging')}
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Message
                      </PlanLimitTooltip>
                      
                      {toc.status === 'blocked' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(toc.user_id, 'active')}
                          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                          data-testid={`unblock-btn-${toc.user_id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Unblock
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(toc.user_id, 'blocked')}
                          className="text-amber-600 border-amber-200 hover:bg-amber-50"
                          data-testid={`block-btn-${toc.user_id}`}
                        >
                          <Ban className="h-4 w-4 mr-1" />
                          Block
                        </Button>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        onClick={() => handleArchiveTOC(toc.user_id)}
                        disabled={isArchiving}
                        data-testid={`archive-btn-${toc.user_id}`}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Notes Preview */}
                  {toc.private_notes && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Private Notes:</p>
                      <p className="text-sm text-slate-700">{toc.private_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </TabsContent>
          
          <TabsContent value="archived">
            {archivedTocList.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Archive className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No archived Subs</h3>
                  <p className="text-sm text-slate-500">
                    Subs you archive will appear here. You can restore them at any time.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {archivedTocList.map((toc) => (
                  <Card key={toc.user_id} className="border-slate-200 bg-slate-50/50">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="h-12 w-12 rounded-full bg-slate-200 flex items-center justify-center">
                            {toc.picture ? (
                              <img src={toc.picture} alt={toc.name} className="h-12 w-12 rounded-full grayscale" />
                            ) : (
                              <User className="h-6 w-6 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-slate-700">{toc.name}</p>
                              <Badge variant="secondary" className="bg-slate-200 text-slate-600">
                                Archived
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">{toc.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <Link to={`/employer/analytics/${schoolId}#sub-detail-${toc.user_id}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-500 border-blue-200 hover:bg-blue-50"
                              data-testid={`view-details-archived-${toc.user_id}`}
                            >
                              <BarChart3 className="h-3.5 w-3.5 mr-1" />
                              View Details
                            </Button>
                          </Link>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => handleUnarchiveTOC(toc.user_id)}
                            disabled={isArchiving}
                            data-testid={`unarchive-btn-${toc.user_id}`}
                          >
                            <ArchiveRestore className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
      <AppFooter role="employer" />
      
      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Private Notes - {selectedTOC?.name}</DialogTitle>
            <DialogDescription>
              These notes are only visible to you and your school staff
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add notes about this substitute teacher..."
              rows={5}
              data-testid="toc-notes-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveNotes} 
              disabled={isSaving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-notes-btn"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Notes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Message {selectedTOC?.name}</DialogTitle>
          </DialogHeader>
          
          {/* Messages List */}
          <div className="flex-1 overflow-y-auto space-y-3 py-4 max-h-64">
            {conversation.length === 0 ? (
              <p className="text-center text-slate-500 text-sm py-8">
                No messages yet. Start the conversation!
              </p>
            ) : (
              conversation.map((msg) => (
                <div 
                  key={msg.message_id}
                  className={`p-3 rounded-lg ${
                    msg.sender_id === user?.user_id 
                      ? 'bg-slate-900 text-white ml-8' 
                      : 'bg-slate-100 text-slate-900 mr-8'
                  }`}
                >
                  <p className="text-sm">{msg.content}</p>
                  <p className={`text-xs mt-1 ${msg.sender_id === user?.user_id ? 'text-slate-400' : 'text-slate-500'}`}>
                    {new Date(msg.created_at).toLocaleString()}
                  </p>
                </div>
              ))
            )}
          </div>
          
          {/* Message Input */}
          <div className="flex gap-2 pt-4 border-t">
            <Input
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type a message..."
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              disabled={isSaving || !planHasFeature(school?.plan_id, 'in_app_messaging')}
              data-testid="message-input"
            />
            <Button
              onClick={handleSendMessage}
              disabled={isSaving || !messageText.trim() || !planHasFeature(school?.plan_id, 'in_app_messaging')}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="send-message-btn"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <MessagingUpgradeDialog open={showMessagingUpgrade} onOpenChange={setShowMessagingUpgrade} />
    </div>
  );
}
